import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import {
  CheckCircle,
  Error as ErrorIcon,
  HourglassEmpty,
} from "@mui/icons-material";
import { CustomButton } from "../../components/common/customButton";
import {
  getExtraction,
  getExtractionImages,
  updateExtractionImage,
} from "../../services/extractionApi";
import { apiGetById } from "../../services/genesisApi/httpsMethods";
import type {
  Extraction,
  ExtractionImage,
  ExtractionImagesResponse,
  ExtractionResult,
  ExtractionStatus,
  ImageExtractionStatus,
} from "../../types/extraction";
import type { PartialDeal } from "../../types/deal";
import { buildExtractionResult } from "../../utils/extractionInspect";
import { shouldRetryImageLoad } from "./imageRetry";
import { useErrorDialog } from "../../hooks/useErrorDialog";
import { ErrorDialog } from "../../components/common/errorDialog";
import { ExtractionImageStatusBar } from "./components/ExtractionImageStatusBar";
import { ExtractionAiClassification } from "./components/ExtractionGptPicks";
import { ExtractionImagesGrid } from "./components/ExtractionImagesGrid";
import CreateDealUpdate, {
  type CreateDealEmbeddedToolbarState,
} from "../deal/CreateDealUpdate";
import { SimpleToolbar } from "../../components/layout/Toolbar";
import "../../assets/css/Text-General.css";

const IMAGE_POLL_INTERVAL = 8000;

const isTerminalImageStatus = (status?: ImageExtractionStatus | null) =>
  status === "completed" ||
  status === "failed" ||
  status === "skipped";

const getStatusIcon = (status: ExtractionStatus) => {
  switch (status) {
    case "completed":
      return <CheckCircle sx={{ fontSize: 16, mr: 0.5 }} />;
    case "failed":
      return <ErrorIcon sx={{ fontSize: 16, mr: 0.5 }} />;
    case "processing":
      return <CircularProgress size={16} sx={{ mr: 0.5 }} />;
    case "pending":
      return <HourglassEmpty sx={{ fontSize: 16, mr: 0.5 }} />;
    default:
      return null;
  }
};

const ExtractionDetail = () => {
  const { extractionId } = useParams<{ extractionId: string }>();
  const navigate = useNavigate();
  const { errorDialog, showErrorFromResponse, hideError } = useErrorDialog();

  const [extraction, setExtraction] = useState<Extraction | null>(null);
  const [extractionResult, setExtractionResult] =
    useState<ExtractionResult | null>(null);
  const [dealForEdit, setDealForEdit] = useState<PartialDeal | null>(null);
  const [dealDataLoading, setDealDataLoading] = useState(false);
  const [imagesData, setImagesData] = useState<ExtractionImagesResponse | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<"deal" | "images">("deal");
  const [selectionUpdating, setSelectionUpdating] = useState(false);
  const [dealToolbar, setDealToolbar] =
    useState<CreateDealEmbeddedToolbarState | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Extraction id whose deal data has been requested. Deal data is loaded once
  // per extraction (when text extraction is first seen as completed) so
  // background polls never remount the embedded deal form and wipe edits.
  const dealDataRequestedForRef = useRef<number | null>(null);
  // Background poll failures show at most one dialog per failure streak.
  const backgroundErrorShownRef = useRef(false);
  // Image ids already refetched after a load error (retry at most once each).
  const retriedImageIdsRef = useRef<Set<number>>(new Set());

  const id = extractionId ? parseInt(extractionId, 10) : NaN;

  const fetchExtraction = useCallback(
    async (options?: { background?: boolean }) => {
      if (!id || Number.isNaN(id)) return null;
      const response = await getExtraction(id);
      if (response?.success && response.data) {
        if (options?.background) backgroundErrorShownRef.current = false;
        setExtraction(response.data);
        return response.data;
      }
      if (options?.background) {
        console.warn(`Background refresh of extraction ${id} failed`, response);
        if (backgroundErrorShownRef.current) return null;
        backgroundErrorShownRef.current = true;
      }
      showErrorFromResponse(response);
      return null;
    },
    [id, showErrorFromResponse],
  );

  const loadDealData = useCallback(async (extractionData: Extraction) => {
    if (extractionData.status !== "completed") {
      setExtractionResult(null);
      setDealForEdit(null);
      return;
    }

    setDealDataLoading(true);
    try {
      const response = await getExtraction(extractionData.id, true);
      if (!response?.success || !response.data) {
        showErrorFromResponse(response);
        return;
      }

      const result = buildExtractionResult(response.data);
      setExtractionResult(result);

      const dealId = response.data.deal_id;
      if (dealId) {
        const dealResponse = await apiGetById("deal", dealId);
        if (dealResponse?.success && dealResponse.data) {
          setDealForEdit(dealResponse.data[0] as PartialDeal);
        }
      } else {
        setDealForEdit(null);
      }
    } finally {
      setDealDataLoading(false);
    }
  }, [showErrorFromResponse]);

  // Load deal data only the first time an extraction is seen as completed.
  const loadDealDataOnce = useCallback(
    (extractionData: Extraction | null) => {
      if (
        !extractionData ||
        extractionData.status !== "completed" ||
        dealDataRequestedForRef.current === extractionData.id
      ) {
        return;
      }
      dealDataRequestedForRef.current = extractionData.id;
      void loadDealData(extractionData);
    },
    [loadDealData],
  );

  const fetchImages = useCallback(
    async (options?: { background?: boolean }) => {
      if (!id || Number.isNaN(id)) return;
      setImagesLoading(true);
      try {
        const response = await getExtractionImages(id);
        if (response?.success && response.data) {
          setImagesData(response.data);
        } else if (options?.background) {
          console.warn(
            `Background refresh of extraction ${id} images failed`,
            response,
          );
        } else {
          showErrorFromResponse(response);
        }
      } finally {
        setImagesLoading(false);
      }
    },
    [id, showErrorFromResponse],
  );

  useEffect(() => {
    retriedImageIdsRef.current = new Set();
  }, [id]);

  useEffect(() => {
    if (!id || Number.isNaN(id)) return;
    setLoading(true);
    fetchExtraction()
      .then((data) => loadDealDataOnce(data))
      .finally(() => setLoading(false));
  }, [id, fetchExtraction, loadDealDataOnce]);

  useEffect(() => {
    if (
      !extraction ||
      extraction.status === "completed" ||
      extraction.status === "failed" ||
      extraction.status === "cancelled"
    ) {
      return;
    }

    const interval = setInterval(() => {
      void fetchExtraction({ background: true }).then((data) =>
        loadDealDataOnce(data),
      );
    }, 10000);

    return () => clearInterval(interval);
  }, [extraction?.status, extraction?.id, fetchExtraction, loadDealDataOnce]);

  useEffect(() => {
    if (!extraction?.extract_images) return;

    const imageStatus = extraction.image_extraction_status;
    if (isTerminalImageStatus(imageStatus)) {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
      if (imageStatus === "completed" || imageStatus === "failed") {
        void fetchImages();
      }
      return;
    }

    // Refresh image status only. Deal data is loaded once, when text
    // extraction first completes, so the embedded deal form keeps its edits.
    pollRef.current = setInterval(() => {
      void fetchExtraction({ background: true }).then((data) =>
        loadDealDataOnce(data),
      );
    }, IMAGE_POLL_INTERVAL);

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [
    extraction?.extract_images,
    extraction?.image_extraction_status,
    fetchExtraction,
    fetchImages,
    loadDealDataOnce,
  ]);

  // A failed image (e.g. expired signed URL) refetches the image list once
  // per image id to pick up fresh URLs; repeated failures are not retried.
  const handleImageError = (image: ExtractionImage) => {
    if (shouldRetryImageLoad(retriedImageIdsRef.current, image.id)) {
      void fetchImages({ background: true });
    }
  };

  const handleToggleSelected = async (image: ExtractionImage) => {
    if (!extraction) return;
    setSelectionUpdating(true);
    try {
      const response = await updateExtractionImage(extraction.id, image.id, {
        is_selected: !image.is_selected,
      });
      if (response?.success) {
        await fetchImages();
      } else {
        showErrorFromResponse(response);
      }
    } finally {
      setSelectionUpdating(false);
    }
  };

  const handleSetPrimary = async (image: ExtractionImage) => {
    if (!extraction) return;
    setSelectionUpdating(true);
    try {
      const response = await updateExtractionImage(extraction.id, image.id, {
        is_hero: true,
      });
      if (response?.success) {
        await fetchImages();
      } else {
        showErrorFromResponse(response);
      }
    } finally {
      setSelectionUpdating(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!extraction) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>Extraction not found.</Typography>
        <CustomButton
          text="Back to list"
          handleButtonPress={() => navigate("/ai-extractions")}
          solid={false}
          isButtonDisabled={false}
        />
      </Box>
    );
  }

  const showImagesTab = extraction.extract_images === true;
  const showDealForm =
    extraction.status === "completed" && extractionResult && !dealDataLoading;

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 10,
          backgroundColor: "background.paper",
          boxShadow: "0px 1px 5px 0px rgba(44, 62, 80, 0.12)",
        }}
      >
        <SimpleToolbar>
          <CustomButton
            text="Back"
            handleButtonPress={() => navigate("/ai-extractions")}
            solid={false}
            isButtonDisabled={false}
            heightOverride="32px"
            widthOverride="90px"
          />
          {dealToolbar && (
            <CustomButton
              text={dealToolbar.submitLabel}
              handleButtonPress={dealToolbar.onSubmit}
              isButtonDisabled={dealToolbar.isSubmitDisabled}
              isLoading={dealToolbar.isLoading}
              solid={true}
            />
          )}
          {dealToolbar?.showDelete && (
            <Box data-position="right">
              <CustomButton
                text="Delete Deal"
                handleButtonPress={dealToolbar.onDelete}
                isButtonDisabled={false}
                isDeleteButton={true}
              />
            </Box>
          )}
        </SimpleToolbar>
      </Box>

      <Box sx={{ px: 3, pt: 2, pb: 0 }}>
        <Typography variant="h5" className="text-general-page-header" sx={{ mb: 1 }}>
          {extraction.file_name}
        </Typography>

        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
          <Chip
            {...(getStatusIcon(extraction.status)
              ? { icon: getStatusIcon(extraction.status)! }
              : {})}
            label={
              extraction.status.charAt(0).toUpperCase() +
              extraction.status.slice(1)
            }
            size="small"
          />
          {showImagesTab && extraction.image_extraction_status && (
            <Chip
              label={`Images: ${
                extraction.image_extraction_status.charAt(0).toUpperCase() +
                extraction.image_extraction_status.slice(1)
              }`}
              size="small"
            />
          )}
        </Box>

        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value)}
          sx={{ borderBottom: 1, borderColor: "divider" }}
        >
          <Tab label="Deal data" value="deal" />
          <Tab label="Images" value="images" disabled={!showImagesTab} />
        </Tabs>
      </Box>

      <Box hidden={activeTab !== "deal"} sx={{ pt: 0 }}>
        {extraction.error_message && (
          <Box sx={{ px: 3, pt: 2 }}>
            <Alert severity="error">{extraction.error_message}</Alert>
          </Box>
        )}
        {extraction.status !== "completed" && (
          <Box sx={{ px: 3, pt: 2 }}>
            <Alert severity="info">
              Text extraction is still in progress. The deal form will appear when
              processing completes.
            </Alert>
          </Box>
        )}
        {extraction.status === "completed" && dealDataLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {showDealForm && (
          <CreateDealUpdate
            key={`extraction-deal-${extraction.id}-${dealForEdit?.id ?? "new"}`}
            deal={dealForEdit ?? undefined}
            deleteOption={!!dealForEdit}
            extractionResult={extractionResult}
            embedded
            onEmbeddedToolbarChange={setDealToolbar}
          />
        )}
      </Box>

      <Box hidden={activeTab !== "images" || !showImagesTab} sx={{ px: 3, pt: 2 }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          Selected images are copied to the deal gallery when you create or update
          the deal from the Deal data tab. Re-applying replaces prior images from
          this extraction.
        </Alert>

        <ExtractionImageStatusBar
          status={extraction.image_extraction_status}
          error={extraction.image_extraction_error}
        />

        {/* Spinner only on the initial load; background refreshes keep the
            grid mounted so images don't reload (and re-error) in a loop. */}
        {imagesLoading && !imagesData && (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {isTerminalImageStatus(extraction.image_extraction_status) &&
          extraction.image_extraction_status === "completed" &&
          imagesData && (
            <>
              <ExtractionAiClassification
                hero={imagesData.hero}
                selected={imagesData.selected}
                onImageError={handleImageError}
                selectionUpdating={selectionUpdating}
                onToggleSelected={(image) => void handleToggleSelected(image)}
                onSetPrimary={(image) => void handleSetPrimary(image)}
              />
              <ExtractionImagesGrid
                images={imagesData.images}
                onImageError={handleImageError}
                selectionUpdating={selectionUpdating}
                onToggleSelected={(image) => void handleToggleSelected(image)}
                onSetPrimary={(image) => void handleSetPrimary(image)}
              />
            </>
          )}
      </Box>

      <ErrorDialog
        open={errorDialog.open}
        onClose={hideError}
        apiResponse={errorDialog.apiResponse}
        customMessage={errorDialog.customMessage}
        title={errorDialog.title}
      />
    </Box>
  );
};

export default ExtractionDetail;
